import { getFallbackConfig, getTreatmentOverrides } from "./experimentConfig";
import {
  Experiment,
  ExperimentFallbackConfig,
  isTrafficType,
  TrafficType,
} from "./types";
import * as Sentry from "@sentry/browser";
import {
  Attributes,
  IClient,
  IManager,
} from "@splitsoftware/splitio/types/splitio";
import merge from "lodash/merge";
import { typecheckConfig } from "./typecheck";
import { alongEnv } from "utils/env";

export function getExperimentContext(args: {
  manager?: IManager;
  clients?: Record<TrafficType, IClient | undefined>;
}) {
  const { manager, clients } = args;

  // We cache experiment results for two reasons:
  //
  // 1. We allow Along to load if the split SDK is loading slowly, and we want
  // to ensure that any fallback values returned by the context are persistent
  // even if the SDK later becomes available.
  //
  // 2. We listen for experiment config updates so we can hard reload the client on
  // route navigate when new config is available. However we don't want the new config
  // to change the results of any experiments that have already been checked until the
  // reload happens (currently there's no way to listen for new config without having
  // the old config receive a live update).
  //
  // NOTE: We don't take attributes into account when caching. We may want to do
  // future work to ensure an experiment isn't checked with different attribute
  // over the course of a session. See https://github.com/chanzuckerberg/along/pull/4340
  // for related discussion.
  type ExperimentCache = {
    [experiment in Experiment]?: ExperimentFallbackConfig<experiment>;
  };
  const cachedResults: ExperimentCache = {};

  const context = {
    getConfig<T extends Experiment>(
      experiment: T,
      attributes?: Attributes,
    ): ExperimentFallbackConfig<T> {
      if (cachedResults[experiment]) {
        return cachedResults[experiment] as ExperimentFallbackConfig<T>;
      }

      let splitConfig = null;
      let retrievalState = "no_client";

      const trafficType = manager?.split(experiment)?.trafficType;
      const client = isTrafficType(trafficType) ? clients?.[trafficType] : null;

      const overrides = getTreatmentOverrides(experiment);
      const fallbackConfig = getFallbackConfig(experiment);

      if (client) {
        retrievalState = "control";
        // TODO: do we want any kind of logging if we run into situations where
        // split is missing an experiment treatment or expected config keys?
        const { treatment, config } = client.getTreatmentWithConfig(
          experiment,
          attributes,
        );

        // If split returns 'control' SDK is still loading or something went wrong.
        // https://help.split.io/hc/en-us/articles/360020528072-Control-treatment
        if (treatment !== "control") {
          retrievalState = "success";
          try {
            splitConfig = JSON.parse(config || "null");
          } catch {
            retrievalState = "parse_error";
            const errorMsg = `Error parsing experiment config from Split. Experiment: ${experiment}, Treatment: ${treatment}`;

            if (alongEnv() === "development") {
              throw new Error(errorMsg);
            } else {
              Sentry.captureMessage(errorMsg);
            }
          }

          // Skip typecheck if there was a parse error (because we already reported an error to Sentry in that case).
          if (
            retrievalState !== "parse_error" &&
            !typecheckConfig(fallbackConfig, splitConfig)
          ) {
            // TODO: might be nice to surface exactly why/where the typecheck failed
            const errorMsg = `Typecheck failed for split config. Experiment: ${experiment}`;
            splitConfig = null; // don't want to override fallback with mismatched data

            if (alongEnv() === "production") {
              Sentry.captureMessage(errorMsg);
            } else {
              throw new Error(errorMsg);
            }
          }
        }
      }

      // Fallback is used if we couldn't retrieve a split config (or it didn't pass typechecking).
      // Overrides come last so they force a specific behavior, if present
      const result = merge(splitConfig ?? fallbackConfig, overrides);

      cachedResults[experiment] = result;
      return result as ExperimentFallbackConfig<T>;
    },
  };

  return context;
}
